import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useCampaignActions } from '../hooks/useCampaignActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { outreachCampaignPath } from '../outreachPaths';
import type { CampaignDetail } from '../outreachTypes';
import { reviewMessages as messages } from './reviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import live from '../live/Campaign.module.scss';
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReviewAllDoneProps {
  accountId: number;
  campaignUuid: string;
  campaign: CampaignDetail | undefined;
  /** The pitches that the artist approved on this visit. */
  approved: number;
}

/**
 * All read (design outreach-v2 h2): the full ring, "Today's 5 pitches are approved", "Open the
 * campaign", and autopilot: on Label it turns on, below Label the offer opens with Label picked.
 */
const ReviewAllDoneComponent: React.FC<ReviewAllDoneProps> = ({ accountId, campaignUuid, campaign, approved }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const plan = useOutreachPlan();
  const openUpgradePrompt = useUpgradePrompt();
  const { updateCampaign, isSaving } = useCampaignActions(accountId);
  const isReviewMode = campaign?.mode !== 'autopilot';
  const turnOn = () => {
    if (!plan.canUseAutopilot) {
      openUpgradePrompt(SMLUpgradeFeature.outreachRate, campaign?.songTitle);
      return;
    }
    updateCampaign({ campaignUuid, mode: 'autopilot' }).catch(console.error);
  };
  return (
    <section className={styles.done} data-testid="review-done">
      <div className={live.ring} style={{ '--p': 100 } as React.CSSProperties} aria-hidden>
        <span className={live.ringValue}>
          {approved}
          <small>/{approved}</small>
        </span>
      </div>
      <h2 className={shared.h2}>
        {approved > 0
          ? intl.formatMessage(messages.doneTitle, { count: approved })
          : intl.formatMessage(messages.doneTitleNone)}
      </h2>
      <p className={`${shared.sub} m-0`}>{intl.formatMessage(messages.doneBody)}</p>
      <div className={`${shared.row} justify-content-center`}>
        <SMLButton
          size="sm"
          outline
          omitCap
          // eslint-disable-next-line no-void
          onClick={() => void navigate(outreachCampaignPath(campaignUuid))}
        >
          {intl.formatMessage(messages.openCampaign)}
        </SMLButton>
        {isReviewMode && !plan.isDemo && (
          <SMLButton size="sm" color="primary" omitCap onClick={turnOn} isLoading={isSaving} testID="review-autopilot">
            {intl.formatMessage(plan.canUseAutopilot ? messages.autopilotOn : messages.autopilotOffer)}
          </SMLButton>
        )}
      </div>
    </section>
  );
};

export const ReviewAllDone = TypedMemo(ReviewAllDoneComponent);
